import moment from 'moment';
import 'moment-timezone';

// Good defaults that cover 99% of Canadian use cases
export const CANONICAL_CA_ZONES = [
    // Provinces
    'America/St_Johns', // Newfoundland
    'America/Halifax', // Atlantic (NS/PEI/NB)
    'America/Toronto', // Eastern (ON/QC)
    'America/Winnipeg', // Central (MB)
    'America/Regina', // Saskatchewan (no DST)
    'America/Edmonton', // Mountain (AB)
    'America/Vancouver', // Pacific (BC)
    'America/Whitehorse', // Yukon (permanent UTC−7)

    // no DST
    'America/Atikokan', // ON pocket (no DST)
    'America/Blanc-Sablon', // QC/Labrador pocket (no DST)
    'America/Creston', // BC pocket (no DST)
    'America/Dawson_Creek', // BC pocket (no DST)
    'America/Fort_Nelson', // BC pocket (no DST)

    // Territories / Nunavut coverage
    'America/Yellowknife', // NWT
    'America/Iqaluit', // NU (Eastern)
    'America/Rankin_Inlet', // NU (Central)
    'America/Cambridge_Bay', // NU (Mountain)
    'America/Goose_Bay', // Labrador (Atlantic w/ DST)
];

// Fallback for Canadian contexts
export const DEFAULT_TIMEZONE = 'America/Toronto';

// Standard format for displaying dates and times in the CMS (ISO 8601 Basic Format)
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * Gets the user's current timezone from their browser using the Intl API
 * @returns User's timezone identifier (e.g., 'America/Toronto')
 * @example
 * const userTz = getUserTimezone(); // 'America/New_York'
 */
export const getUserTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Gets the user's timezone if it's in our Canadian zones list, otherwise returns fallback
 * @param fallback - Fallback timezone if user's timezone is not Canadian (defaults to 'America/Toronto')
 * @returns Canadian timezone identifier
 * @example
 * getUserTimezoneOrFallback(); // 'America/Toronto' (if user is not in Canada)
 * getUserTimezoneOrFallback('America/Vancouver'); // 'America/Vancouver' (if user is not in Canada)
 */
export const getUserTimezoneOrFallback = (
    fallback: string = DEFAULT_TIMEZONE
): string => {
    const userTz = getUserTimezone();
    return CANONICAL_CA_ZONES.includes(userTz) ? userTz : fallback;
};

/**
 * Converts a local timezone date to UTC moment object
 * @param date - moment object or date string in YYYY-MM-DD HH:mm:ss format
 * @param timezone - IANA timezone identifier (e.g., 'America/Toronto')
 * @returns UTC moment object or null if input is null/undefined
 * @example
 * convertLocalToUtcMoment('2023-12-25 15:30:00', 'America/Toronto')
 * // Returns moment object representing '2023-12-25 20:30:00 UTC' (EST = UTC-5)
 */
export const convertLocalToUtcMoment = (
    date: moment.Moment | string | null | undefined,
    timezone: string
): moment.Moment | null => {
    if (!date) return null;

    const localDateString =
        typeof date === 'string' ? date : date.format(DATE_TIME_FORMAT);
    return moment.tz(localDateString, timezone).utc();
};

/**
 * Converts a local timezone date to UTC ISO string
 * @param date - moment object or date string in YYYY-MM-DD HH:mm:ss format
 * @param timezone - IANA timezone identifier (e.g., 'America/Toronto')
 * @returns UTC ISO string or null if input is null/undefined
 * @example
 * convertLocalToUtcString('2023-12-25 15:30:00', 'America/Toronto')
 * // Returns '2023-12-25T20:30:00.000Z' (EST = UTC-5)
 */
export const convertLocalToUtcString = (
    date: moment.Moment | string | null | undefined,
    timezone: string
): string | null => {
    if (!date) return null;
    return convertLocalToUtcMoment(date, timezone)?.toISOString() ?? null;
};

/**
 * Converts a UTC date to a moment object in the specified local timezone
 * @param utcDate - UTC ISO string (e.g., '2023-12-25T20:30:00.000Z') or UTC moment object
 * @param timezone - IANA timezone identifier (e.g., 'America/Toronto')
 * @returns moment object in the specified timezone or null if input is null/undefined
 * @example
 * convertUtcToLocalMoment('2023-12-25T20:30:00.000Z', 'America/Toronto')
 * // Returns moment object representing '2023-12-25 15:30:00' in EST (UTC-5)
 */
export const convertUtcToLocalMoment = (
    utcDate: string | moment.Moment | null | undefined,
    timezone: string
): moment.Moment | null => {
    if (!utcDate) return null;

    // If it's already a moment, assume it's UTC and convert to timezone
    if (moment.isMoment(utcDate)) return utcDate.tz(timezone);

    // If it's a string, parse as UTC and convert to timezone
    return moment.tz(utcDate, timezone);
};

/**
 * Converts a UTC date to a formatted local timezone string
 * @param utcDate - UTC ISO string (e.g., '2023-12-25T20:30:00.000Z') or UTC moment object
 * @param timezone - IANA timezone identifier (e.g., 'America/Toronto')
 * @param format - moment format string (defaults to DATE_TIME_FORMAT: 'YYYY-MM-DD HH:mm:ss')
 * @returns formatted local timezone string or null if input is null/undefined
 * @example
 * convertUtcToLocalString('2023-12-25T20:30:00.000Z', 'America/Toronto')
 * // Returns '2023-12-25 15:30:00' (EST = UTC-5)
 * x
 * convertUtcToLocalString('2023-12-25T20:30:00.000Z', 'America/Toronto', 'MMM DD, YYYY h:mm A')
 * // Returns 'Dec 25, 2023 3:30 PM'
 */
export const convertUtcToLocalString = (
    utcDate: string | moment.Moment | null | undefined,
    timezone: string,
    format = DATE_TIME_FORMAT
): string | null => {
    return convertUtcToLocalMoment(utcDate, timezone)?.format(format) ?? null;
};
